import { useMemo } from "react"
import { map } from "lodash"
import { useOutletContext } from "react-router-dom"
import { percentageFormatter, oneDecimalPercentageFormatter } from "helpers/formatters/numbers"
import { createRiskSingleDateQuery, SPECIFIC_RISK, FACTORS_RISK, TOTAL_RISK } from "gatherers/single-date/risk"
import { createRiskMultiDateQuery, METRICS } from "gatherers/multi-date/risk"
import CompareGraph from "../renderers/compare-graph"
import { useSelectedMetric } from "../hooks/selected-metric"
import { useAvailableMetrics } from "../hooks/available-metrics"
import DataIntegratedSection from "./data-integrated-section"
export default function CompareRisk() {
  const context = useOutletContext()
  const { modelMetadata, modelId, startDate, endDate, selectedDate, positionSets, selectedMetric: queryStringSelectedMetric, modelMetrics } = context

  const specificMetrics = {
    id: "risk",
    label: "Risk",
    options: map(METRICS, ({ label }, id) => ({ label, id, category: "risk" })),
  }
  const availableMetrics = useAvailableMetrics(modelMetrics, specificMetrics, true)
  const selectedMetric = useSelectedMetric(availableMetrics, queryStringSelectedMetric)

  const currentDate = selectedDate || endDate

  const graph = useMemo(() => ({
    id: "graph",
    name: "Graph",
    renderer: CompareGraph,
    // Compare has no intraday mode; pass false explicitly rather than relying on the arg being omitted.
    metrics: map(positionSets, set => createRiskMultiDateQuery(modelId, set.queryPositionSet, set.queryBase, startDate, endDate, false)),
    availableMetrics,
    selectedMetric,
    positionSetsMetadata: positionSets,
    tickFormatter: oneDecimalPercentageFormatter,
    separator: true,
    defaultUndefinedValue: 0,
  }), [positionSets, availableMetrics, selectedMetric, modelId, startDate, endDate])

  const sections = useMemo(() => ([
    graph,
    {
      id: "total",
      name: "Total Risk",
      separator: true,
      metrics: map(positionSets, set =>
        createRiskSingleDateQuery(modelId, set.queryPositionSet, set.queryBase, currentDate, { metrics: [TOTAL_RISK] }),
      ),
      rowCount: 0,
    }, {
      id: "riskDecomp",
      name: "Risk Decomposition",
      separator: true,
      metrics: map(positionSets, set =>
        createRiskSingleDateQuery(modelId, set.queryPositionSet, set.queryBase, currentDate, { metrics: [SPECIFIC_RISK, FACTORS_RISK] }),
      ),
      defaultExpanded: true,
      rowCount: 2,
    }, ...map(modelMetadata.categories, category => ({
      id: category.id,
      name: category.label,
      color: category.color,
      isGroup: true,
      sorted: true,
      metrics: map(positionSets, set => createRiskSingleDateQuery(modelId, set.queryPositionSet, set.queryBase, currentDate, { category: category.id })),
    })),
  ]),
  // [TODO] how to improve reference integrity of position sets
  [graph, positionSets, modelMetadata?.categories, modelId, currentDate])

  return sections.map((section, index) => {
    const configKey = `risk-${section.id}`
    return (
      <DataIntegratedSection
        key={configKey}
        configKey={configKey}
        section={section}
        positionSets={positionSets}
        index={index}
        defaultFormatter={percentageFormatter}
      />
    )
  })
}
